import type { SearchPerformancePagesSortBy } from './api/searchPerformanceApi';
import type { PagesTabKey } from './pages';

export type GscOAuthMethod = 'popup' | 'same_tab';

// Doubles as the connection error's code: the wizard renders copy from it
// (GscConnectionErrors.ts), so a state the customer saw is always one we count.
export type GscConnectFailureReason =
  | 'request_failed'
  | 'stuck_source'
  | 'popup_closed'
  | 'access_denied'
  | 'scope_denied'
  | 'google_error'
  | 'relay_unavailable'
  | 'token_exchange_failed'
  | 'token_revoked'
  | 'invalid_state'
  | 'property_access_denied'
  | 'upstream_error'
  | 'oauth_error'
  | 'malformed_result';

export type GscActivationFailureReason =
  | 'no_property_access'
  | 'domain_cap_reached'
  | 'already_connected'
  // The same-domain guard (CEN-216): two selected properties describe one
  // domain, or a live property elsewhere already syncs a selected domain (the
  // replaceable conflict behind the replace offer).
  | 'overlapping_selection'
  | 'domain_already_connected'
  | 'not_in_plan'
  | 'request_failed';

// Tracked separately from a failed connect: the OAuth round trip succeeded,
// there is simply nothing to sync.
export type GscConnectionDeadEndReason =
  | 'no_properties'
  | 'no_matching_properties'
  | 'insufficient_permission'
  | 'session_expired'
  | 'session_load_failed'
  | 'property_access_lost';

// What the dead end's primary button does: re-run Google sign-in, or retry
// loading the selection session (only offered for `session_load_failed`).
export type GscDeadEndAction = 'reauthenticate' | 'retry_load';

export type GscPropertyToggleAction = 'selected' | 'deselected';

// We only ever emit `manual`, the discrete disconnect we observe. A revocation
// has no such client-side moment: it surfaces later as a broken connection
// state and is tracked through the reconnect events, not this one.
export type GscDisconnectMethod = 'manual' | 'revoked';

export type SearchPerformanceWidget = 'kpis' | 'paths' | 'pages';

export type SearchPerformanceLoadTrigger =
  | 'initial'
  | 'refresh'
  | 'property_switch'
  | 'window_rollover'
  | 'retry'
  | 'tab_change'
  | 'interaction';

export type SearchPerformancePagesLoadStage = 'rows' | 'columns';

export type SearchPerformancePagesLoadSourceType = 'GSC' | 'PRERENDER';

type SearchPerformanceLoadMeasurement = {
  duration_ms: number;
  trigger: SearchPerformanceLoadTrigger;
};

type SearchPerformancePagesLoadContext = {
  widget: 'pages';
  tab: PagesTabKey;
  source_type: SearchPerformancePagesLoadSourceType;
};

type SearchPerformanceLoadSubject<PagesDetail> =
  | { widget: Exclude<SearchPerformanceWidget, 'pages'> }
  | (SearchPerformancePagesLoadContext & PagesDetail);

type SearchPerformanceDataLoaded = SearchPerformanceLoadMeasurement & {
  has_data: boolean;
} & SearchPerformanceLoadSubject<{ rows_visible_ms?: number; partial: boolean }>;

type SearchPerformanceDataLoadFailed = SearchPerformanceLoadMeasurement & {
  status?: number | string;
} & SearchPerformanceLoadSubject<{ stage: SearchPerformancePagesLoadStage }>;

export type SearchPerformanceSortDirection = 'asc' | 'desc';

// The empty-state CTA the customer clicked. Only `connect_gsc` fires here: an
// upgrade click is the shared `UpgradeClicked` billing event, tagged with a
// Search Performance `cta_location` so its origin is still distinguishable.
export type SearchPerformanceEmptyStateCtaType = 'connect_gsc';

// KPI group-card ids in Mixpanel vocabulary. The parent "Unique URLs analyzed"
// card has no CTA, so it never appears here.
export type SearchPerformanceKpiCardName =
  | 'crawled_with_impressions'
  | 'crawled_no_impressions'
  | 'not_prerender_served_with_impressions';

// Performance-by-path sortable columns in Mixpanel vocabulary — snake_case, and
// diverging from the BFF's camelCase sort keys. `path` is sortable in the table
// though the ticket's column list omitted it.
export type SearchPerformancePathSortColumn =
  | 'path'
  | 'crawled_with_impressions'
  | 'crawled_no_impressions'
  | 'not_prerender_served_with_impressions'
  | 'ctr'
  | 'delivery_errors';

type CommaJoinedPropertyUrls = string;

export type SearchPerformanceAnalyticsEventProperties = {
  // The connect CTA on the empty (no-connection) state. Renamed from
  // "Connect With Google Clicked" (PRE-3271 #6) to fold it into the empty-state
  // CTA funnel; the upgrade CTA is tracked via `UpgradeClicked`, not here.
  'Search Performance: Empty State CTA Clicked': {
    cta_type: SearchPerformanceEmptyStateCtaType;
  };
  'Search Performance: Connect Failed': {
    reason: GscConnectFailureReason;
    method?: GscOAuthMethod;
    error_detail?: string;
  };
  'Search Performance: Google Authenticated': {
    method: GscOAuthMethod;
  };
  'Search Performance: Connection Dead End': {
    reason: GscConnectionDeadEndReason;
    total_property_count?: number;
  };
  'Search Performance: Domain Search Used': void;
  'Search Performance: Property Toggled': {
    action: GscPropertyToggleAction;
    property_url: string;
    selected_count: number;
  };
  'Search Performance: Selection Limit Reached': {
    limit: number;
  };
  'Search Performance: Domain Step Back Clicked': {
    selected_count: number;
  };
  'Search Performance: Dead End Action Clicked': {
    reason: GscConnectionDeadEndReason;
    action: GscDeadEndAction;
  };
  'Search Performance: Activate Domains Clicked': {
    selected_count: number;
    domain: CommaJoinedPropertyUrls;
  };
  'Search Performance: Domains Activated': {
    selected_count: number;
    paired_count: number;
    failed_count: number;
    domain: CommaJoinedPropertyUrls;
  };
  'Search Performance: Activation Failed': {
    reason: GscActivationFailureReason;
    selected_count: number;
    domain: CommaJoinedPropertyUrls;
  };
  'Search Performance: Integration Connect Clicked': void;
  // Separate from the first connect: the only measure of how often Google
  // revocation actually costs customers.
  'Search Performance: Integration Reconnect Clicked': void;
  // Same destination as the Integrations card's Reconnect, tracked separately
  // (PRE-3235) so the two entry points can be told apart.
  'Search Performance: Dashboard Reconnect Clicked': void;
  'Search Performance: Dashboard Manage Domains Clicked': void;
  'Search Performance: Integration Disconnected': {
    method: GscDisconnectMethod;
  };
  'Search Performance: Manage Domains Clicked': void;
  'Search Performance: Manage Domains Saved': {
    added_count: number;
    removed_count: number;
  };
  'Search Performance: Dashboard Viewed': void;
  // Tracked by the page itself: the route is a child of the Integrations
  // layout, and ViewEventListener only reads top-level routes.
  'Search Performance: Settings Viewed': void;
  'Search Performance: Data Load Failed': SearchPerformanceDataLoadFailed;
  'Search Performance: Data Loaded': SearchPerformanceDataLoaded;
  'Search Performance: Retry Clicked': {
    widget: SearchPerformanceWidget;
  };
  'Search Performance: Domain Selector Changed': {
    from_domain: string;
    to_domain: string;
    // The post-switch value. Overrides the tracker's auto-attached `domain`,
    // which reads the pre-switch selection at the moment onChange fires.
    domain: string;
  };
  'Search Performance: KPI Card Clicked': {
    kpi_card_name: SearchPerformanceKpiCardName;
    destination_tab: PagesTabKey;
  };
  'Search Performance: Pages Tab Viewed': {
    tab: PagesTabKey;
  };
  'Search Performance: Pages URL Searched': {
    tab: PagesTabKey;
  };
  'Search Performance: Pages Sort Changed': {
    tab: PagesTabKey;
    column: SearchPerformancePagesSortBy;
    direction: SearchPerformanceSortDirection;
  };
  'Search Performance: Pages Filter Changed': {
    filter_name: 'http_status';
    new_value: string;
    old_value: string;
  };
  'Search Performance: Pages Clear Filters Clicked': {
    tab: PagesTabKey;
  };
  'Search Performance: Page URL Clicked': {
    tab: PagesTabKey;
    url_hash: string;
  };
  'Search Performance: Path Sort Changed': {
    column: SearchPerformancePathSortColumn;
    direction: SearchPerformanceSortDirection;
  };
  'Search Performance: Path Row Reviewed': {
    path_prefix: string;
  };
};

export type SearchPerformanceAnalyticsEvent = keyof SearchPerformanceAnalyticsEventProperties;

export const CONNECTION_SURFACE_EVENTS: ReadonlySet<SearchPerformanceAnalyticsEvent> =
  new Set<SearchPerformanceAnalyticsEvent>([
    'Search Performance: Empty State CTA Clicked',
    'Search Performance: Connect Failed',
    'Search Performance: Google Authenticated',
    'Search Performance: Connection Dead End',
    'Search Performance: Domain Search Used',
    'Search Performance: Property Toggled',
    'Search Performance: Selection Limit Reached',
    'Search Performance: Domain Step Back Clicked',
    'Search Performance: Dead End Action Clicked',
    'Search Performance: Activate Domains Clicked',
    'Search Performance: Domains Activated',
    'Search Performance: Activation Failed',
    'Search Performance: Integration Connect Clicked',
    'Search Performance: Integration Reconnect Clicked',
    'Search Performance: Integration Disconnected',
    'Search Performance: Manage Domains Clicked',
    'Search Performance: Manage Domains Saved',
    'Search Performance: Settings Viewed',
  ]);
